/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { mergeStyleSets } from '@fluentui/react';
import { CommandBarButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type CluProjectsCommandBarProps = TableCommandBarProps<CluProjectStore> & {
    onCopyProject: () => void;
    canCreateProject: boolean;
    serviceType: CluProjectType;
    onCreateProject: () => void;
    onExportProject: () => void;
    onImportProject: () => void;
    isCreateProjectModalHidden: boolean;
    onDeleteProjects: () => boolean | void;
};

const messages = defineMessages({
    delete: { id: 'clu.cluProjectsTableCommandBar.delete', defaultMessage: 'Delete' },
    import: { id: 'clu.cluProjectsTableCommandBar.import', defaultMessage: 'Import' },
    export: { id: 'clu.cluProjectsTableCommandBar.export', defaultMessage: 'Export' },
    copyProject: { id: 'clu.cluProjectsTableCommandBar.copyProject', defaultMessage: 'Copy project' },
    createNewProject: { id: 'clu.cluProjectsTableCommandBar.createNewProject', defaultMessage: 'Create new project' },
    splitButtonAriaLabel: { id: 'clu.cluProjectsTableCommandBar.splitButtonAriaLabel', defaultMessage: 'More creation options' },
    warning: {
        id: 'clu.cluProjectsTableCommandBar.warning',
        defaultMessage: 'Please select a subscription and an authoring resource first'
    }
});

const commandBarTheme = getScopedTheme('ConversationAppsTableCommandBar');
const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    label: { fontWeight: 'normal' },
    menuIcon: { lineHeight: '20px' },
    rootDisabled: { backgroundColor: 'transparent' },
    root: { backgroundColor: 'transparent', border: 'none', height: '100%', padding: '0 4px' }
});

export const CluProjectsTableCommandBar = observer((props: CluProjectsCommandBarProps) => {
    const {
        query = '',
        serviceType,
        onCopyProject,
        onQueryChange,
        onCreateProject,
        onExportProject,
        onImportProject,
        onDeleteProjects,
        canCreateProject,
        selectedItems = [],
        isCreateProjectModalHidden
    } = props;
    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();
    const createProjectItemIdRef = React.useRef<string>(getId());
    const importProjectItemIdRef = React.useRef<string>(getId());
    const copyProjectItemIdRef = React.useRef<string>(getId());

    const { isSignedIn } = rootStore.authStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeleteProject } = mapUserRoleToUserActions(userRole);

    React.useEffect(() => () => dequeueTeachingBubble('CluCreateProject'), []);

    React.useEffect(() => {
        if (isSignedIn && canWrite && isCreateProjectModalHidden && !uiStore.teachingBubbles.CluCreateProject && canCreateProject) {
            createTeachingBubble(
                'CluCreateProject',
                localize(
                    serviceType === 'Conversation'
                        ? cluTeachingBubbleMessages.createNewCLUProjectBody
                        : cluTeachingBubbleMessages.createNewOrchestrationProjectBody
                ),
                localize(cluTeachingBubbleMessages.createNewProjectTitle),
                `#${createProjectItemIdRef.current}`,
                true
            );
        } else {
            dequeueTeachingBubble('CluCreateProject');
        }
    }, [Object.values(uiStore.teachingBubbles), isCreateProjectModalHidden, canCreateProject, canWrite]);

    const getCreateButton = (disabled: boolean, renderedInOverflow: boolean) => (
        <CommandBarButton
            disabled={disabled}
            onClick={onCreateProject}
            id={createProjectItemIdRef.current}
            text={localize(messages.createNewProject)}
            data-automation-id="createCLUProjectButton"
            styles={mergeStyleSets(buttonStyles, getCommandBarButtonStyles(renderedInOverflow))}
            iconProps={{ iconName: 'Add', styles: { root: { color: !disabled && commandBarTheme.activeIconColor } } }}
        />
    );

    const getCopyButton = (disabled: boolean, renderedInOverflow: boolean) => (
        <CommandBarButton
            disabled={disabled}
            onClick={onCopyProject}
            iconProps={{ iconName: 'Copy' }}
            id={copyProjectItemIdRef.current}
            text={localize(messages.copyProject)}
            data-automation-id="copyCLUProjectButton"
            styles={getCommandBarButtonStyles(renderedInOverflow)}
        />
    );

    const getImportButton = (disabled: boolean, renderedInOverflow: boolean) => (
        <CommandBarButton
            disabled={disabled}
            onClick={onImportProject}
            text={localize(messages.import)}
            iconProps={{ iconName: 'Upload' }}
            id={importProjectItemIdRef.current}
            data-automation-id="importCLUProjectButton"
            styles={getCommandBarButtonStyles(renderedInOverflow)}
        />
    );

    const commands: ICommandBarItemProps[] = [
        {
            key: 'create',
            onRender: (item: ICommandBarItemProps) =>
                isSignedIn && canWrite ? (
                    <LuisTooltip hidden={canCreateProject} content={localize(messages.warning)}>
                        {getCreateButton(!canCreateProject || item.disabled, item.renderedInOverflow)}
                    </LuisTooltip>
                ) : (
                    <RBACTooltip
                        hidden={canWrite || item.disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="createCLUProjectButtonLanguageToolTip"
                        componentSurroundedByToolTip={getCreateButton(!isSignedIn || !canWrite || item.disabled, item.renderedInOverflow)}
                    />
                )
        },
        {
            key: 'import',
            onRender: (item: ICommandBarItemProps) =>
                isSignedIn && canWrite ? (
                    <LuisTooltip hidden={canCreateProject} content={localize(messages.warning)}>
                        {getImportButton(!canCreateProject || item.disabled, item.renderedInOverflow)}
                    </LuisTooltip>
                ) : (
                    <RBACTooltip
                        hidden={canWrite || item.disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="importCLUProjectButtonLanguageToolTip"
                        componentSurroundedByToolTip={getImportButton(!isSignedIn || !canWrite || item.disabled, item.renderedInOverflow)}
                    />
                )
        },
        { key: 'export', role: 'button', name: localize(messages.export), iconProps: { iconName: 'Download' }, onClick: onExportProject },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canDeleteProject || item.disabled}
                    data-automation-id="deleteCLUProjectButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onDeleteProjects}
                            text={localize(messages.delete)}
                            name={localize(messages.delete)}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteCLUProjectButton"
                            disabled={!canDeleteProject || item.disabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    if (FeatureToggles.CopyProject) {
        const copyProject: ICommandBarItemProps[] = [
            {
                key: 'copy',
                onRender: (item: ICommandBarItemProps) =>
                    canWrite && isSignedIn ? (
                        <LuisTooltip hidden={canCreateProject} content={localize(messages.warning)}>
                            {getCopyButton(!canCreateProject || item.disabled, item.renderedInOverflow)}
                        </LuisTooltip>
                    ) : (
                        <RBACTooltip
                            hidden={canWrite || item.disabled}
                            displayedUserRole={displayedUserRole}
                            data-automation-id="copyCLUProjectButtonLanguageToolTip"
                            componentSurroundedByToolTip={getCopyButton(!isSignedIn || !canWrite || item.disabled, item.renderedInOverflow)}
                        />
                    )
            }
        ];

        commands.splice(3, 0, ...copyProject);
    }

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['create', 'import'],
        ['delete', 'export', 'copy'],
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} includeSearchBar searchBarAlwaysExpanded query={query} onQueryChange={onQueryChange} />;
});
